import {
	type NavbarBranchItem,
	navbarBranchQueryKeys,
} from "@src/components/board/queries/fetch-ranklist";
import { syncFolderHasChildrenFromBranchQueryKey } from "@src/components/board/queries/navbar-branch-has-children";
import type { NavbarSection } from "@src/gql/graphql";
import type { QueryClient } from "@tanstack/react-query";

export type NavbarBranchSnapshot = {
	queryKey: readonly unknown[];
	data: NavbarBranchItem[];
};

/**
 * Optimistically remove an item from every navbar branch cache that
 * contains it. Returns snapshots for rollback / targeted invalidate.
 */
export async function optimisticallyRemoveNavbarItem(
	queryClient: QueryClient,
	itemKey: string,
): Promise<NavbarBranchSnapshot[]> {
	await queryClient.cancelQueries({
		queryKey: navbarBranchQueryKeys.all(),
	});

	const snapshots: NavbarBranchSnapshot[] = [];
	const queries = queryClient.getQueriesData<NavbarBranchItem[]>({
		queryKey: navbarBranchQueryKeys.all(),
	});

	for (const [queryKey, data] of queries) {
		if (!data?.some((item) => item.key === itemKey)) {
			continue;
		}
		snapshots.push({ queryKey, data: [...data] });
		queryClient.setQueryData<NavbarBranchItem[]>(queryKey, (old) =>
			old?.filter((item) => item.key !== itemKey),
		);
		if (queryKey.length >= 3) {
			syncFolderHasChildrenFromBranchQueryKey(
				queryClient,
				queryKey[1] as NavbarSection,
				queryKey,
			);
		}
	}

	return snapshots;
}

export function restoreNavbarBranchSnapshots(
	queryClient: QueryClient,
	snapshots: NavbarBranchSnapshot[],
): void {
	for (const { queryKey, data } of snapshots) {
		queryClient.setQueryData(queryKey, data);
	}
}

export function invalidateNavbarBranchSnapshots(
	queryClient: QueryClient,
	snapshots: NavbarBranchSnapshot[],
): void {
	for (const { queryKey } of snapshots) {
		void queryClient.invalidateQueries({ queryKey });
	}
}

/**
 * Optimistically update an item's icon in every navbar branch cache that
 * contains it. Returns snapshots for rollback / targeted invalidate.
 */
export async function optimisticallyUpdateNavbarItemIcon(
	queryClient: QueryClient,
	itemKey: string,
	icon: NavbarBranchItem["icon"],
): Promise<NavbarBranchSnapshot[]> {
	await queryClient.cancelQueries({
		queryKey: navbarBranchQueryKeys.all(),
	});

	const snapshots: NavbarBranchSnapshot[] = [];
	const queries = queryClient.getQueriesData<NavbarBranchItem[]>({
		queryKey: navbarBranchQueryKeys.all(),
	});

	for (const [queryKey, data] of queries) {
		if (!data?.some((item) => item.key === itemKey)) {
			continue;
		}
		snapshots.push({ queryKey, data: [...data] });
		queryClient.setQueryData<NavbarBranchItem[]>(queryKey, (old) =>
			old?.map((item) =>
				item.key === itemKey ? { ...item, icon: icon ?? null } : item,
			),
		);
	}

	return snapshots;
}
